import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Icon from '../../icon';
import SurveyCore from './SurveyCore';

import { graphMutateWithoutUser } from '../../../requests/graphql';
import { summonGlobalMessenger } from '../../../utility/dispatchers';

import layout from '../../../styles/global_ui/layout';
import typography from '../../../styles/global_ui/typography';
import style from './popover.css';

const c = {
  popupContainer: `${style.popupContainer}`,
  container: `${layout.flexColumn} ${layout.gutter10} ${style.container}`,
  iconWrapper: `${style.iconWrapper}`,
};

const Survey = ({
  align,
  alignOffset,
  allowRepeatEntries = true,
  element,
  onOpenChange,
  onSubmit,
  sideOffset,
  surveyCss,
  surveyJson,
  surveySlug,
  triggerIconName,
  triggerTitle,
  version,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const customCss = { completedPage: c.container, ...surveyCss };

  const handleOpenChange = useCallback(
    (isOpening) => {
      // dispatch openSurvey event when opening a survey so that other surveys know not to open
      if (isOpening) {
        const event = new Event('openSurvey');
        document.dispatchEvent(event);
      } else {
        const event = new Event('closeSurvey');
        document.dispatchEvent(event);
      }

      setIsOpen(isOpening);
      onOpenChange?.(isOpening);
    },
    [onOpenChange, setIsOpen],
  );

  const popup
    = element === 'embed'
      ? undefined
      : {
          align,
          alignOffset,
          classList: { container: c.popupContainer, trigger: c.iconWrapper },
          hasClose: true,
          open: isOpen,
          onOpenChange: handleOpenChange,
          sideOffset,
          disabled: isDisabled,
          trigger: (
            <Icon
              className={`${typography.iconClickableStaticWhite}`}
              disabled={isDisabled}
              id={`${surveySlug}-icon`}
              name={triggerIconName}
              size={16}
            />
          ),
          triggerAria: { triggerTitle },
        };

  const saveSurveyResults = useCallback(
    ({ content, status, isUpdating }) => {
      if (!content || Object.keys(content).length === 0) return;

      const response = {
        content: JSON.stringify(content),
        status,
        survey_slug: surveySlug,
        version: version,
      };

      const template = !allowRepeatEntries && isUpdating ? 'update_survey_result' : 'create_survey_result';

      graphMutateWithoutUser(
        { t: template },
        { result: response },
      ).catch((err) => {
        summonGlobalMessenger({
          msg: 'Oops, something went wrong. Try again later.',
          type: 'error',
        });
        return err;
      });

      onSubmit?.();
    },
    [allowRepeatEntries, onSubmit, surveySlug, version],
  );

  // We need to disable the popup if any auto opening survey is mounted. If more than one survey is initialized, the data might get tied to the wrong survey model
  useEffect(() => {
    const handleOpenSurvey = () => setIsDisabled(true);
    const handleCloseSurvey = () => setIsDisabled(false);

    document.addEventListener('openSurvey', handleOpenSurvey);
    document.addEventListener('closeSurvey', handleCloseSurvey);

    return () => {
      document.removeEventListener('openSurvey', handleOpenSurvey);
      document.removeEventListener('closeSurvey', handleCloseSurvey);
    };
  }, []);

  useEffect(() => {
    // When the component mounts dispatch openSurvey event so that other surveys know not to open
    // if element is popup, wait to dispatch until popup / dialog opens
    if (element === 'embed') {
      const event = new Event('openSurvey');
      document.dispatchEvent(event);

      return () => {
        const event = new Event('closeSurvey');
        document.dispatchEvent(event);
      };
    }
  // empty dep array for mount / unmount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SurveyCore
      allowRepeatEntries={allowRepeatEntries}
      element={element}
      onSubmit={saveSurveyResults}
      popup={popup}
      surveyCss={customCss}
      surveyJson={surveyJson}
      surveySlug={surveySlug}
      version={version}
    />
  );
};

export default Survey;

Survey.propTypes = {
  align: PropTypes.string,
  alignOffset: PropTypes.number,
  allowRepeatEntries: PropTypes.bool, // They can take the survey as many times as they want. If false, then their original entry is updated.
  element: PropTypes.oneOf(['dialog', 'embed', 'popup']).isRequired,
  onOpenChange: PropTypes.func,
  onSubmit: PropTypes.func,
  sideOffset: PropTypes.number,
  surveyCss: PropTypes.shape({}),
  surveyJson: PropTypes.shape({}).isRequired,
  surveySlug: PropTypes.string.isRequired,
  triggerIconName: PropTypes.string,
  triggerTitle: PropTypes.string,
  version: PropTypes.number.isRequired,
};
